import type { ImportAssetWarning } from '@/lib/email-conversion/email-asset-types'

/**
 * Every source format an email can be imported from. Isomorphic: the chat
 * composer, the canvas dialog, the public API contract and the server
 * normalizer all read the same union.
 */
export const EMAIL_SOURCE_FORMATS = [
  'html',
  'mjml',
  'eml',
  'react-email',
] as const

export type EmailSourceFormat = (typeof EMAIL_SOURCE_FORMATS)[number]

export function isEmailSourceFormat(
  value: unknown
): value is EmailSourceFormat {
  return (
    typeof value === 'string' &&
    (EMAIL_SOURCE_FORMATS as ReadonlyArray<string>).includes(value)
  )
}

/**
 * What the server normalizer hands the import runner. HTML-family sources
 * (html, mjml, eml) land as the exact-pair lane; a React Email module lands
 * as a JSX-native authored head.
 */
export type PreparedEmailSource =
  | {
      kind: 'html'
      html: string
      baseUrl?: string
      filename?: string
      /** EML Subject (≤ 200 chars) — a title default, below an explicit title. */
      title?: string
      /** EML Subject (≤ 250 chars, the `subjectLine` input bound). */
      subjectLine?: string
      /** Source-level notices (dropped attachments, unresolved cid refs). */
      warnings?: ReadonlyArray<ImportAssetWarning>
    }
  | {
      kind: 'jsx'
      /** Storage-shape bare `<Html>…</Html>`; NOT yet safety-validated. */
      jsx: string
      filename?: string
      title?: string
      /** Lossy fold outcomes (`location: 'module'`): dropped Tailwind classes, an unread theme file, a left-out component. */
      warnings?: ReadonlyArray<ImportAssetWarning>
    }

export type EmailSourceNormalizeFailure =
  | 'empty_source'
  | 'conversion_failed'
  | 'unsupported_source'
